import { Check } from "lucide-react";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getThemeOptionsForSettings, isThemeNew } from "src/lib/themeOptions";
import type { AppDispatch } from "src/store";
import { selectTheme, setTheme } from "src/store/themeSlice";

const nunito = { fontFamily: "'Nunito', system-ui, sans-serif" } as const;

export const SettingsHeader: React.FC<{
  title: string;
  description: string;
}> = ({ title, description }) => {
  return (
    <header className="shrink-0 rounded-2xl border border-white/10 bg-gray-950/90 p-4 shadow-sm backdrop-blur-sm">
      <h2 className="text-lg font-extrabold tracking-tight text-white">
        {title}
      </h2>
      <p className="mt-1 max-w-2xl text-sm leading-5 text-gray-400">
        {description}
      </p>
    </header>
  );
};

interface SettingsThemesSectionProps {
  isActive: boolean;
  refreshKey: number;
}

const SettingsThemesSection: React.FC<SettingsThemesSectionProps> = ({
  isActive,
  refreshKey,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentTheme = useSelector(selectTheme);
  const themeOptions = useMemo(() => getThemeOptionsForSettings(), []);

  return (
    <section className="flex h-full min-h-0 flex-col gap-4" style={nunito}>
      <SettingsHeader
        title="Appearance"
        description="Choose the look and feel of your dashboard. Your theme preference is saved on this device."
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
        <div
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
          role="radiogroup"
          aria-label="Dashboard theme"
        >
          {themeOptions.map((theme) => {
            const selected = currentTheme === theme.key;
            const showNew = isThemeNew(theme);

            return (
              <button
                key={theme.key}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => dispatch(setTheme(theme.key))}
                className={[
                  "group relative overflow-hidden rounded-3xl border text-left",
                  "bg-gray-950/80 shadow-lg shadow-black/20",
                  "transition-transform duration-200 ease-out",
                  "active:translate-y-0",
                  "focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-400/40",
                  selected
                    ? "border-teal-400 shadow-[0_0_0_1px_rgba(45,212,191,0.35),0_18px_35px_rgba(0,0,0,0.25)]"
                    : "border-white/10 hover:-translate-y-0.5 hover:border-white/20 hover:bg-gray-900/80 hover:shadow-xl",
                ].join(" ")}
              >
                <div className="relative">
                  <div className={`h-24 w-full ${theme.preview}`} aria-hidden />

                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-gray-950/80 to-transparent" />

                  {selected && (
                    <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-teal-400 px-2.5 py-1 text-xs font-extrabold text-gray-950 shadow-lg">
                      <Check
                        className="h-3.5 w-3.5"
                        strokeWidth={3}
                        aria-hidden
                      />
                      Current
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-4 p-4">
                  <span
                    className={[
                      "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/15",
                      "bg-gradient-to-br shadow-inner shadow-white/10",
                      theme.colors,
                    ].join(" ")}
                    aria-hidden
                  >
                    {selected && (
                      <Check
                        className="h-5 w-5 text-white drop-shadow"
                        strokeWidth={3}
                        aria-hidden
                      />
                    )}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <p className="truncate text-base font-extrabold text-white">
                        {theme.label}
                      </p>
                      {showNew && (
                        <span className="shrink-0 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-amber-300">
                          New
                        </span>
                      )}
                    </div>

                    <p className="mt-1 line-clamp-2 text-sm leading-5 text-gray-400">
                      {theme.description}
                    </p>
                  </div>
                </div>

                <div
                  className={[
                    "absolute inset-0 rounded-3xl pointer-events-none transition-opacity",
                    selected
                      ? "opacity-100 shadow-[inset_0_0_0_1px_rgba(45,212,191,0.45)]"
                      : "opacity-0 group-hover:opacity-100 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]",
                  ].join(" ")}
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SettingsThemesSection;
